import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type {Audience} from "crqlar-react-common/dist/models/audience/audience.model";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import AudienceDialog from "../../../marketing/AudienceDialog";
import type {MarketingInterestAudienceV4} from "../../models/marketing-v4";
import InterestAudienceCard from "./InterestAudienceCard";

const LOADING_CARD_COUNT = 12;

interface InterestAudiencesSectionProps {
    cards?: MarketingInterestAudienceV4[];
    loading: boolean;
}

export function InterestAudiencesSection({cards = [], loading}: InterestAudiencesSectionProps) {
    const {t} = useTranslation();
    const [currentAudience, setCurrentAudience] = useState<Audience>();
    const readOnly = useReadOnly();

    return (
        <>
            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    alignItems: "stretch",
                    gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        sm: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                    },
                }}
            >
                {loading
                    ? Array.from({length: LOADING_CARD_COUNT}, (_, index) => (
                        <InterestAudienceCard key={index} loading/>
                    ))
                    : cards.length > 0 ? cards.map(card => (
                        <InterestAudienceCard
                            key={card.key}
                            card={card}
                            loading={false}
                            readOnly={readOnly}
                            onCreateAudience={setCurrentAudience}
                        />
                    )) : (
                        <Typography variant="body2" color="text.secondary" sx={{gridColumn: "1 / -1"}}>
                            {t("No interest audiences available")}
                        </Typography>
                    )}
            </Box>

            {currentAudience && (
                <AudienceDialog
                    key={String(
                        currentAudience.id
                        ?? currentAudience.tags?.sourceAudience
                        ?? currentAudience.name
                        ?? "new-audience"
                    )}
                    audience={currentAudience}
                    open
                    onClose={() => setCurrentAudience(undefined)}
                    onSaved={() => setCurrentAudience(undefined)}
                />
            )}
        </>
    );
}

export default InterestAudiencesSection;
